// Action runner for the page designer.
//
// runAction(action, ctx) walks the steps sequentially, awaits each, and
// branches on call_api success/error. All HTTP goes through fetch — relative
// URLs run with credentials so cookie-auth works; absolute URLs run without
// credentials so we don't leak them cross-origin.

import type {
  ActionStep, CallApiStep, PageAction, PageInput, TxnActionStep, TxnUpdateStep,
} from '@twasta/page-schema'
import type { ActionState } from './runtime'
import { evaluateInputs } from './validate'
import { substituteVars, getPath, toDisplayString, type VarSource } from './substitute'
import { refreshAfter, runTxnActionStep, runTxnUpdateStep, type TxnStepResult } from './txnSteps'
import type { ConfirmHelpers } from '../txn/ActionConfirmModal'
import type { ActionConfirmInfo, RunActionResponse } from '../txn/contracts'

// ─── External context plumbed in by PageDesignerPage ───────────────

export interface ActionCtx {
  // Snapshots of current page model state.
  inputs: PageInput[]
  // Values are arbitrary — JSON variables hold objects/arrays. Action
  // steps that send `values` as a typed body coerce per-input below.
  values: Record<string, unknown>

  // State mutators (lifted to PageDesignerPage).
  setInput: (name: string, value: string) => void
  // Write a RAW value (object/array/scalar) into a page variable, preserving
  // structure — unlike setInput which stores a substituted string. Used by
  // call_api's `save_response_var` so a JSON response can feed bindings / an
  // embed's recordVar intact.
  setVarRaw: (name: string, value: unknown) => void
  clearInputs: (names?: string[]) => void
  setFieldError: (name: string, message: string) => void
  clearFieldError: (name: string) => void

  // DOM helpers.
  focusInput: (name: string) => boolean

  // Per-action state map (loading/error/response). The runner mutates this
  // through setActionState; consumers read via getActionState.
  setActionState: (name: string, state: ActionState) => void
  getActionState: (name: string) => ActionState | undefined

  // Variable resolution — built from values + actionState, used to resolve
  // {{key}} tokens in step strings (urls, headers, body, messages).
  resolve: VarSource

  // Toast helpers (success/error/info).
  showMessage: (level: 'success' | 'error' | 'info', text: string) => void

  // Navigation (used by inline ButtonClick.navigate, not an action step).
  navigate: (objType: string, objName: string) => void

  // AbortSignal for the current action invocation.
  signal: AbortSignal

  // Surface-relative URL for the server-side IOFlow invoke proxy (used by
  // call_api steps with `ioflow_api` set — the browser never holds the
  // IOFlow invocation key). Platform surfaces:
  //   (name) => `/api/integration/invoke/${project}/${name}`
  // Standalone runtime client:
  //   (name) => `/app/${project}/api/integration/invoke/${name}`
  // Absent → ioflow_api steps fail with a clear message.
  integrationInvokeUrl?: (apiName: string) => string

  // ── Record-changing steps (txn_action / txn_update / refresh_source) ──
  // The engine transport this surface already uses for transaction screens:
  // the platform's live-preview / site-txn executor, the downloaded runtime's
  // /crud/execute. Throws the surface's own error shape on refusal. Absent →
  // the steps fail with a clear message.
  execute?: (objName: string, action: string, payload: Record<string, unknown>) => Promise<unknown>
  // Re-read data sources (`undefined` = every runtime-bound source).
  refreshSources?: (ids?: string[]) => Promise<void>
  // The shared action-confirmation modal (useActionConfirm().confirmAction),
  // for an action button that asks "are you sure" / a password / an OTP.
  confirmAction?: (info: ActionConfirmInfo, helpers: ConfirmHelpers) => Promise<RunActionResponse>
}

// ─── Public entry point ────────────────────────────────────────────

export async function runAction(action: PageAction, ctx: ActionCtx): Promise<void> {
  // Per-action concurrency guard handled by the caller (PageDesignerPage)
  // via getActionState — runAction trusts that it's been gated.
  ctx.setActionState(action.name, { loading: true })
  try {
    await runSteps(action.steps, ctx, action.name)
    // Preserve any saved response/status; just clear loading.
    const prev = ctx.getActionState(action.name) || { loading: false }
    ctx.setActionState(action.name, { ...prev, loading: false })
  } catch (err) {
    // A step threw something we didn't catch (e.g. validate failure with no
    // recovery, or an unhandled exception). Surface as a toast.
    const message = err instanceof Error ? err.message : String(err)
    const prev = ctx.getActionState(action.name) || { loading: false }
    ctx.setActionState(action.name, { ...prev, loading: false, error: message })
    if (!(err instanceof ValidationFailed)) {
      ctx.showMessage('error', message)
    }
  }
}

// ─── Step dispatcher ───────────────────────────────────────────────

class ValidationFailed extends Error {
  constructor() { super('validation failed'); this.name = 'ValidationFailed' }
}

async function runSteps(steps: ActionStep[], ctx: ActionCtx, actionName: string): Promise<void> {
  for (const step of steps) {
    if (ctx.signal.aborted) return
    await runStep(step, ctx, actionName)
  }
}

async function runStep(step: ActionStep, ctx: ActionCtx, actionName: string): Promise<void> {
  switch (step.kind) {
    case 'validate': {
      const errors = evaluateInputs(ctx.inputs, ctx.values, step.names)
      if (errors.length === 0) return
      // Apply field errors and (optionally) focus the first failing input.
      for (const e of errors) ctx.setFieldError(e.name, e.message)
      if (step.on_fail_focus !== false) {
        ctx.focusInput(errors[0].name)
      }
      throw new ValidationFailed()
    }

    case 'set_input': {
      const v = substituteVars(step.value, ctx.resolve)
      ctx.setInput(step.name, v)
      return
    }

    case 'clear_inputs': {
      ctx.clearInputs(step.names)
      return
    }

    case 'set_field_error': {
      const msg = substituteVars(step.message, ctx.resolve)
      ctx.setFieldError(step.name, msg)
      return
    }

    case 'focus_input': {
      ctx.focusInput(step.name)
      return
    }

    case 'show_message': {
      ctx.showMessage(step.level, substituteVars(step.text, ctx.resolve))
      return
    }

    case 'call_api': {
      await runCallApi(step, ctx, actionName)
      return
    }

    case 'txn_action':
    case 'txn_update': {
      const r = step.kind === 'txn_action'
        ? await runTxnActionStep(step, ctx)
        : await runTxnUpdateStep(step, ctx)
      if (r === null) return                         // nothing to act on
      await handleTxnResult(step, ctx, actionName, r)
      return
    }

    case 'refresh_source': {
      await refreshAfter(step.sources && step.sources.length ? step.sources : undefined, ctx)
      return
    }

    default: {
      // Exhaustiveness — unknown step kinds are no-ops.
      const _exhaustive: never = step
      void _exhaustive
      return
    }
  }
}

// ─── call_api ──────────────────────────────────────────────────────

async function runCallApi(step: CallApiStep, ctx: ActionCtx, actionName: string): Promise<void> {
  // IOFlow-routed call: POST through the surface's server-side invoke proxy
  // (the IOFlow invocation key lives server-side only). url/headers ignored.
  const ioflowApi = (step.ioflow_api || '').trim()
  let url: string
  let method: string
  let isAbsolute = false
  if (ioflowApi) {
    if (!ctx.integrationInvokeUrl) {
      await handleApiResult(step, ctx, actionName, {
        ok: false, status: undefined, response: undefined,
        errorMessage: `IOFlow API "${ioflowApi}" is not available on this surface`,
      })
      return
    }
    url = ctx.integrationInvokeUrl(ioflowApi)
    method = 'POST'
  } else {
    url = substituteVars(step.url, ctx.resolve).trim()
    if (!url) {
      ctx.showMessage('error', 'call_api: URL is empty')
      return
    }
    method = step.method || 'POST'
    isAbsolute = /^https?:\/\//i.test(url)
  }

  // Resolve headers (substitute each value)
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (!ioflowApi && step.headers) {
    for (const [k, v] of Object.entries(step.headers)) {
      headers[k] = substituteVars(v, ctx.resolve)
    }
  }

  // Body: explicit > coerced inputValues for non-GET. GET sends no body.
  let bodyInit: BodyInit | undefined = undefined
  if (method !== 'GET') {
    let bodyData: unknown
    if (step.body !== undefined && step.body !== null) {
      bodyData = deepSubstitute(step.body, ctx.resolve)
    } else {
      bodyData = coerceInputs(ctx.inputs, ctx.values)
    }
    bodyInit = JSON.stringify(bodyData)
  }

  let res: Response
  try {
    res = await fetch(url, {
      method,
      headers,
      body: bodyInit,
      credentials: isAbsolute ? 'omit' : 'include',
      signal: ctx.signal,
    })
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return
    const message = err instanceof Error ? err.message : 'Network error'
    await handleApiResult(step, ctx, actionName, {
      ok: false, status: undefined, response: undefined, errorMessage: message,
    })
    return
  }

  // Try JSON, fall back to text.
  let parsed: unknown
  try {
    const text = await res.text()
    if (text) {
      try { parsed = JSON.parse(text) } catch { parsed = text }
    }
  } catch {
    parsed = undefined
  }

  // The invoke proxy wraps the flow's body as {success, response} — unwrap
  // so save_response_to/save_response_var see the flow's own payload.
  if (ioflowApi && parsed && typeof parsed === 'object'
      && (parsed as Record<string, unknown>).success === true
      && 'response' in (parsed as Record<string, unknown>)) {
    parsed = (parsed as Record<string, unknown>).response
  }

  if (res.ok) {
    await handleApiResult(step, ctx, actionName, {
      ok: true, status: res.status, response: parsed,
    })
  } else {
    const errorMessage = pickErrorMessage(parsed, res.status)
    await handleApiResult(step, ctx, actionName, {
      ok: false, status: res.status, response: parsed, errorMessage,
    })
  }
}

interface ApiResult {
  ok: boolean
  status?: number
  response?: unknown
  errorMessage?: string
}

async function handleApiResult(
  step: CallApiStep, ctx: ActionCtx, actionName: string, r: ApiResult,
): Promise<void> {
  // Save response under the requested key (success OR error — both can carry
  // a body the author wants to surface, e.g. validation messages).
  if (step.save_response_to) {
    ctx.setActionState(step.save_response_to, {
      loading: false,
      response: r.response,
      status: r.status,
      error: r.ok ? undefined : r.errorMessage,
    })
  }

  // Write the parsed response OBJECT straight into a page variable (structure
  // preserved). Only on success — an error body shouldn't masquerade as data.
  if (r.ok && step.save_response_var) {
    ctx.setVarRaw(step.save_response_var, r.response)
  }

  // Update the action's own state too so {{actionName.error}} works.
  if (!r.ok) {
    const prev = ctx.getActionState(actionName) || { loading: false }
    ctx.setActionState(actionName, { ...prev, loading: false, error: r.errorMessage, status: r.status })
  }

  const branch = r.ok ? step.on_success : step.on_error
  if (branch && branch.length > 0) {
    await runSteps(branch, ctx, actionName)
  } else if (!r.ok) {
    // No on_error branch defined — fall back to a toast.
    ctx.showMessage('error', r.errorMessage || `Request failed (${r.status ?? 'network'})`)
  }
}

// The record steps report like call_api: response under `save_response_to`,
// `{{action.error}}` on failure, then the on_success / on_error branch (or a
// toast when there is none). A success re-reads the data the step names, so
// the page shows the change before on_success runs.
async function handleTxnResult(
  step: TxnActionStep | TxnUpdateStep, ctx: ActionCtx, actionName: string, r: TxnStepResult,
): Promise<void> {
  if (step.save_response_to) {
    ctx.setActionState(step.save_response_to, {
      loading: false, response: r.response, status: r.status, error: r.ok ? undefined : r.errorMessage,
    })
  }
  if (!r.ok) {
    const prev = ctx.getActionState(actionName) || { loading: false }
    ctx.setActionState(actionName, { ...prev, loading: false, error: r.errorMessage, status: r.status })
    if (step.on_error && step.on_error.length) await runSteps(step.on_error, ctx, actionName)
    else ctx.showMessage('error', r.errorMessage || 'The change was not saved')
    // A refused change may still mean the screen is stale (someone else got
    // there first): re-read so it shows the truth.
    await refreshAfter(step.refresh, ctx)
    return
  }
  await refreshAfter(step.refresh, ctx)
  if (step.on_success && step.on_success.length) await runSteps(step.on_success, ctx, actionName)
  else if (r.notice) ctx.showMessage('success', r.notice)
}

function pickErrorMessage(body: unknown, status?: number): string {
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>
    for (const key of ['message', 'detail', 'error', 'msg']) {
      const v = obj[key]
      if (typeof v === 'string' && v.length > 0) return v
    }
  }
  if (typeof body === 'string' && body.length > 0) return body
  return `Request failed (${status ?? 'network'})`
}

// ─── Helpers ───────────────────────────────────────────────────────

// Walk an object/array body and substitute {{vars}} in string leaves.
function deepSubstitute(value: unknown, vars: VarSource): unknown {
  if (typeof value === 'string') return substituteVars(value, vars)
  if (Array.isArray(value)) return value.map((v) => deepSubstitute(v, vars))
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = deepSubstitute(v, vars)
    }
    return out
  }
  return value
}

// Coerce the raw inputValues map into a typed object based on the declared
// PageInput types. Used as the default request body for non-GET call_api
// steps. JSON variables pass through as-is; scalar inputs (which may have
// been received as numbers/booleans already) get string-aware coercion.
export function coerceInputs(
  inputs: PageInput[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const inp of inputs) {
    const raw = values[inp.name]
    if (inp.type === 'json') {
      // Already structured at runtime; if it came back from disk as a
      // JSON-encoded string, parse it now.
      if (typeof raw === 'string') {
        try { out[inp.name] = raw === '' ? null : JSON.parse(raw) }
        catch { out[inp.name] = raw }
      } else {
        out[inp.name] = raw ?? null
      }
      continue
    }
    const rawStr = raw == null ? '' : typeof raw === 'string' ? raw : String(raw)
    switch (inp.type) {
      case 'number': {
        if (rawStr === '') { out[inp.name] = null; break }
        const n = Number(rawStr)
        out[inp.name] = Number.isFinite(n) ? n : null
        break
      }
      case 'checkbox':
        out[inp.name] = rawStr === 'true' || raw === true
        break
      case 'date':
      case 'select':
      case 'text':
      default:
        out[inp.name] = rawStr
    }
  }
  return out
}

// Build a resolver function that unifies inputValues, page-meta vars, and
// per-action state lookups. Used as the {{var}} substitution source.
export function buildResolver(args: {
  values: Record<string, unknown>
  pageMeta: Record<string, string>                 // {title, description, ...}
  actionState: Record<string, ActionState | undefined>
  /** Asked last — the host's page resolver (`me.*`, `{{<dataSource>.<COL>}}`,
   *  `._loaded`), so an action step reads the same tokens page text and
   *  visibility conditions do. */
  fallback?: (key: string) => unknown
  /** The variables at the click (inside a repeat: the clicked ROW, chained
   *  to the page). Answers before page variables — except for an action's
   *  own state, which is live here and only a snapshot in the scope. */
  scope?: VarSource
  /** A few named values of the invocation itself ({{drop.lane}}, {{new_rows}}). */
  extra?: Record<string, unknown>
}): VarSource {
  const { values, pageMeta, actionState, fallback, scope, extra } = args
  return (key: string) => {
    if (extra && Object.prototype.hasOwnProperty.call(extra, key)) return extra[key]
    if (scope) {
      const head = key.includes('.') ? key.slice(0, key.indexOf('.')) : key
      if (!actionState[head]) {
        const v = typeof scope === 'function'
          ? scope(key)
          : (Object.prototype.hasOwnProperty.call(scope, key) ? (scope as Record<string, unknown>)[key] : undefined)
        if (v !== undefined) return v
      }
    }
    // Direct input value
    if (Object.prototype.hasOwnProperty.call(values, key)) return values[key]
    // Page-meta direct match
    if (Object.prototype.hasOwnProperty.call(pageMeta, key)) return pageMeta[key]

    // Dotted path: first segment may be an action-state key.
    const dot = key.indexOf('.')
    if (dot > 0) {
      const head = key.slice(0, dot)
      const tail = key.slice(dot + 1)
      const st = actionState[head]
      if (st) {
        if (tail === 'loading') return st.loading ? 'true' : 'false'
        if (tail === 'error')   return st.error || ''
        if (tail === 'status')  return st.status === undefined ? '' : String(st.status)
        // Otherwise treat tail as a JSON path into the response body.
        const v = getPath(st.response, tail)
        return v === undefined ? undefined : toDisplayString(v)
      }
    }

    // Whole-key match against an action's response (no path) → stringify.
    const st = actionState[key]
    if (st) return toDisplayString(st.response)

    return fallback ? fallback(key) : undefined
  }
}
